/**
 * Billing service layer — M-02 / M-04.
 *
 * THE ONLY FILE that handles checkout and plan logic. Components must
 * import from here — never call Stripe SDK or supabase directly from
 * a component (CLAUDE.md rule 1).
 *
 * Mock vs live behaviour is driven by VITE_STRIPE_MODE:
 *   'mock'  (default) — returns canned data, no network calls to Stripe.
 *   'live'  — will hit real Stripe (M-03, wired by Magdy / Phase 2).
 *
 * Swap path for Phase 2:
 *   1. Magdy creates a Supabase Edge Function (or NestJS route)
 *      POST /api/stripe/create-checkout-session  → { url: string }
 *      POST /api/stripe/create-portal-session    → { url: string }
 *   2. Replace the mock branches below with fetch() calls to those endpoints.
 *   3. Set VITE_STRIPE_MODE=live in the environment.
 *   4. Remove markCurrentUserAsPro() — the webhook becomes the authority.
 *   Components, routes, and hooks do NOT change.
 */

import { supabase } from "../supabase";
import type {
  BillingResult,
  CheckoutSession,
  Plan,
  PortalSession,
} from "../../types/billing";

// ─── Config ─────────────────────────────────────────────────────────────────

const STRIPE_MODE = (import.meta.env.VITE_STRIPE_MODE ?? "mock") as
  | "mock"
  | "live";

/** True when the service is running in mock mode. */
export function isMockMode(): boolean {
  return STRIPE_MODE !== "live";
}

// ─── Internal helpers ────────────────────────────────────────────────────────

/** Single getSession() call — returns both userId and accessToken together. */
async function getAuthContext(): Promise<{
  userId: string;
  accessToken: string;
} | null> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session?.user.id || !session?.access_token) return null;
  return { userId: session.user.id, accessToken: session.access_token };
}

/** Used only by getUserPlan() which only needs the user ID. */
async function getCurrentUserId(): Promise<string | null> {
  const ctx = await getAuthContext();
  return ctx?.userId ?? null;
}

function randomMockId(): string {
  return crypto.randomUUID().replace(/-/g, "").slice(0, 24);
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Get the authenticated user's current plan.
 *
 * Reads from user_profiles.plan. Returns 'free' for unauthenticated
 * users or if the row/column doesn't exist yet.
 *
 * Phase 2 swap: replace the supabase.from() call with a GET to
 * /api/user/plan (or read from the JWT claims).
 */
export async function getUserPlan(): Promise<Plan> {
  const userId = await getCurrentUserId();
  if (!userId) return "free";

  const { data, error } = await supabase
    .from("user_profiles")
    .select("plan")
    .eq("user_id", userId)
    .single();

  if (error || !data) return "free";

  // Narrow to our known Plan union; default to 'free' for unknown values.
  const raw = (data as { plan?: unknown }).plan;
  if (raw === "pro") return "pro";
  return "free";
}

/**
 * Create a Stripe Checkout session and return the redirect URL.
 *
 * Mock mode: immediately returns an internal success URL so the full
 * UI flow can be exercised without a real Stripe account.
 *
 * Live mode (Phase 2): POSTs to the backend endpoint that creates
 * a real Stripe Checkout session and returns the hosted URL.
 *
 * @param priceId  The Stripe price ID to check out (e.g. VITE_STRIPE_PRICE_ID_PRO).
 */
export async function createCheckoutSession(
  priceId: string,
): Promise<BillingResult<CheckoutSession>> {
  if (!priceId) {
    return {
      ok: false,
      error: { code: "unknown", message: "No price ID provided." },
    };
  }

  if (isMockMode()) {
    const sessionId = `cs_mock_${randomMockId()}`;
    return {
      ok: true,
      data: { url: `/upgrade/success?mock=1&session_id=${sessionId}` },
    };
  }

  // ── Live path (Phase 2) ──────────────────────────────────────────────────
  // Replace this block when Magdy wires the real endpoint.
  try {
    const auth = await getAuthContext();
    if (!auth) {
      return {
        ok: false,
        error: {
          code: "unauthenticated",
          message: "You must be signed in to upgrade.",
        },
      };
    }
    const { accessToken } = auth;

    const res = await fetch("/api/stripe/create-checkout-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ priceId }),
    });

    if (!res.ok) {
      return {
        ok: false,
        error: {
          code: "unknown",
          message: `Checkout failed (${res.status}). Please try again.`,
        },
      };
    }

    const json = (await res.json()) as { url?: string };
    if (!json.url) {
      return {
        ok: false,
        error: { code: "unknown", message: "Invalid response from server." },
      };
    }

    return { ok: true, data: { url: json.url } };
  } catch (err) {
    const msg =
      err instanceof Error && err.message.toLowerCase().includes("fetch")
        ? "Network error. Check your connection and try again."
        : "Checkout failed. Please try again.";
    return {
      ok: false,
      error: { code: "network", message: msg },
    };
  }
}

/**
 * Create a Stripe Customer Portal session and return the redirect URL.
 *
 * Mock mode: redirects to /settings (the logical equivalent).
 * Live mode (Phase 2): POSTs to the backend endpoint.
 */
export async function createPortalSession(): Promise<
  BillingResult<PortalSession>
> {
  if (isMockMode()) {
    return { ok: true, data: { url: "/settings" } };
  }

  // ── Live path (Phase 2) ──────────────────────────────────────────────────
  try {
    const auth = await getAuthContext();
    if (!auth) {
      return {
        ok: false,
        error: {
          code: "unauthenticated",
          message: "You must be signed in to manage billing.",
        },
      };
    }
    const { accessToken } = auth;

    const res = await fetch("/api/stripe/create-portal-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!res.ok) {
      return {
        ok: false,
        error: {
          code: "unknown",
          message: `Portal failed (${res.status}). Please try again.`,
        },
      };
    }

    const json = (await res.json()) as { url?: string };
    if (!json.url) {
      return {
        ok: false,
        error: { code: "unknown", message: "Invalid response from server." },
      };
    }

    return { ok: true, data: { url: json.url } };
  } catch (err) {
    const msg =
      err instanceof Error && err.message.toLowerCase().includes("fetch")
        ? "Network error. Check your connection and try again."
        : "Portal failed. Please try again.";
    return {
      ok: false,
      error: { code: "network", message: msg },
    };
  }
}

/**
 * DELETE IN PHASE 2 — when real Stripe webhook lands, the webhook becomes
 * the only authority for flipping plan to 'pro'. This client-side flip
 * is mock-mode only and must be removed.
 *
 * Tracked: issue #150
 *
 * Mark the current user's plan as 'pro' in user_profiles.
 *
 * MOCK MODE ONLY. This function exists solely to close the demo loop:
 * when a user completes the mock checkout, we flip their plan so the
 * app reflects the Pro state without waiting for a real Stripe webhook.
 *
 * In live mode this function is a no-op and logs an error. The Stripe
 * webhook (M-03) is the real authority for plan changes in production.
 */
export async function markCurrentUserAsPro(): Promise<BillingResult<null>> {
  // Fail loudly on misconfigured VITE_STRIPE_MODE (e.g. "LIVE", "live ", "").
  // Only "mock" and "live" are valid. Anything else means the env is broken.
  if (STRIPE_MODE !== "mock" && STRIPE_MODE !== "live") {
    throw new Error(
      `[billing] VITE_STRIPE_MODE has an invalid value: "${STRIPE_MODE}". ` +
        'Expected "mock" or "live". Fix the environment config before continuing.',
    );
  }

  if (!isMockMode()) {
    console.error(
      "[billing] markCurrentUserAsPro() called in live mode. " +
        "This function must only run in mock mode. Ignoring.",
    );
    return {
      ok: false,
      error: {
        code: "mock_only_violation",
        message: "markCurrentUserAsPro() is not allowed in live mode.",
      },
    };
  }

  const userId = await getCurrentUserId();
  if (!userId) {
    return {
      ok: false,
      error: {
        code: "unauthenticated",
        message: "You must be signed in to upgrade.",
      },
    };
  }

  const { error } = await supabase
    .from("user_profiles")
    .upsert({ user_id: userId, plan: "pro" }, { onConflict: "user_id" });

  if (error) {
    console.error("[billing] markCurrentUserAsPro upsert failed:", error.message);
    return {
      ok: false,
      error: {
        code: "unknown",
        message: "Unable to update plan. Please try again.",
      },
    };
  }

  return { ok: true, data: null };
}
